const resources = {
  "en": {
    "transactionsHub": {
      "pageTitle": "Transactions hub",
      "pageDescription": "Capture new movement quickly and keep recent activity readable.",
      "actionsTitle": "Quick actions",
      "actionsDescription": "Start an expense or income entry without leaving the transaction workspace.",
      "addExpense": "Add expense",
      "addIncome": "Add income",
      "addTransfer": "Account transfer",
      "recentTitle": "Recent transactions",
      "recentDescription": "Review the latest movement, fixes, and goal funding in one place.",
      "expenseModalTitle": "Add new expense",
      "incomeModalTitle": "Add new income",
      "loadMore": "Load more",
      "loadingMore": "Loading more transactions...",
      "scrollForMore": "Scroll to load more transactions",
      "endOfList": "You reached the end of the recent transactions list.",
      "expenseSuccess": "Expense saved successfully.",
      "incomeSuccess": "Income saved successfully.",
      "empty": "No transactions yet. Try adding one to get started.",
      "loadError": "Unable to load recent transactions.",
      "unnamed": "Transaction",
      "type": {
        "expense": "Expense",
        "income": "Income",
        "transfer": "Transfer",
        "other": "Transaction"
      },
      "categories": {
        "manualTransfer": "Manual transfer",
        "reconcile": "Reconciliation adjustment",
        "goal": "Goal contribution"
      },
      "accountBadge": {
        "cardLabel": "Card",
        "transferLabel": "Transfer route",
        "from": "From",
        "to": "To",
        "unknown": "Unknown card"
      },
      "lockedBoundary": "Locked · before {{date}}",
      "locked": "Locked",
      "revaluePending": "Revaluation pending",
      "valuation": {
        "estimatedIn": "Estimated in {{currency}}"
      },
      "undoGoalContribution": "Undo contribution",
      "undoTransfer": "Undo transfer",
      "undoConfirmTitle": "Undo goal contribution?",
      "undoConfirmDescription": "This removes the transfer and updates the goal progress.",
      "undoConfirmOk": "Undo contribution",
      "undoTransferConfirmTitle": "Undo transfer?",
      "undoTransferConfirmDescription": "This removes the transfer entry.",
      "undoTransferConfirmOk": "Undo transfer",
      "undoConfirmCancel": "Keep transfer",
      "undoExpiredHint": "Undo is available for 24 hours after the transfer.",
      "undoSuccess": "Contribution removed.",
      "undoError": "Unable to undo contribution.",
      "reviewQueue": {
        "title": "Review queue",
        "alertTitle": "A few transactions still need your category",
        "alertBody": "{{count}} transactions need a final category before reports can place them correctly.",
        "operationalTitle": "The transaction itself is already saved correctly",
        "operationalBody": "You only need to choose how this should appear in reports. This will not change the original amount, date, or ledger flow.",
        "breakdownTitle": "What still needs attention",
        "loading": "Checking transactions that still need your decision...",
        "loadError": "Unable to load review queue.",
        "resolve": "Save category",
        "markReviewed": "Leave without category",
        "resolveSuccess": "Review resolved.",
        "bulkResolveSuccess": "Review cohort resolved.",
        "resolveError": "Unable to resolve review item.",
        "currentMeaning": "Current report category",
        "bulkCurrentMeaning": "Current category for this group",
        "unassigned": "Not selected yet",
        "businessPlaceholder": "Choose the best category",
        "reportingPlaceholder": "Choose a reports-only category",
        "bulkTitle": "Fix repeated items together",
        "bulkBody": "{{count}} repeated groups can be updated in one step.",
        "bulkResolve": "Save for this group",
        "bulkTag": "{{count}} transactions",
        "whatToDo": "Pick the category that best describes this transaction in your reports.",
        "bulkWhatToDo": "Choose one category and apply it to this whole group.",
        "sources": {
          "manualEntry": "Manual entry",
          "recurringTask": "Recurring task",
          "reconciliation": "Balance adjustment",
          "other": "Needs review"
        },
        "reasons": {
          "reconciliationUnclassified": {
            "label": "Balance adjustment needs a reports label",
            "body": "This entry fixes your balance. Add a reports-only category only if you want to group it somewhere."
          },
          "recurringMissingMeaning": {
            "label": "Recurring item needs a category",
            "body": "This repeating transaction still needs the category you want reports to use."
          },
          "missingUserCategory": {
            "label": "Category still missing",
            "body": "Choose the category that best describes this transaction so reports place it correctly."
          },
          "generic": {
            "label": "Needs your review",
            "body": "This transaction still needs a final category for reporting."
          }
        }
      },
      "transfer": {
        "title": "Account transfer",
        "defaultNote": "Account transfer",
        "unknownAccount": "Unknown account",
        "noticeTitle": "Move money between your own accounts",
        "noticeBody": "Transfers change account balances, but they are not counted as income or expense in reports.",
        "notEnoughAccountsTitle": "Add one more account first",
        "notEnoughAccountsBody": "A transfer needs at least two active accounts.",
        "fromEyebrow": "Money leaves",
        "toEyebrow": "Money arrives",
        "fields": {
          "from": {
            "label": "From",
            "placeholder": "Choose the source account",
            "required": "Choose where the money comes from"
          },
          "to": {
            "label": "To",
            "placeholder": "Choose the destination account",
            "required": "Choose where the money goes"
          },
          "amount": {
            "label": "Amount",
            "placeholder": "0.00",
            "required": "Enter transfer amount",
            "invalid": "Enter a valid amount greater than zero",
            "hint": "Only same-currency transfers are supported."
          },
          "date": {
            "label": "Date",
            "required": "Choose transfer date"
          },
          "note": {
            "label": "Note",
            "placeholder": "Optional note, e.g. cash top-up"
          }
        },
        "balance": {
          "current": "Current balance",
          "available": "Available",
          "currency": "Currency"
        },
        "preview": {
          "fromAfter": "Source after transfer",
          "toAfter": "Destination after transfer",
          "availableAfter": "Available after: {{amount}}",
          "notIncome": "Internal movement, not income"
        },
        "actions": {
          "submit": "Create transfer",
          "cancel": "Cancel",
          "swap": "Swap accounts"
        },
        "messages": {
          "success": "Transfer completed.",
          "error": "Unable to create transfer: {{error}}",
          "unknownError": "Unexpected error",
          "accountsLoadError": "Unable to load accounts for transfer."
        }
      },
      "forms": {
        "expense": {
          "fields": {
            "amount": {
              "label": "Amount",
              "placeholder": "Enter amount",
              "required": "Amount is required",
              "hint": "Decimals are supported. Use a dot, for example: 12.34"
            },
            "category": {
              "label": "Category",
              "placeholder": "Select category",
              "required": "Category is required"
            },
            "currency": {
              "label": "Currency",
              "placeholder": "Select currency",
              "required": "Currency is required"
            },
            "currencyRequired": "Currency is required",
            "assetType": {
              "label": "Asset type",
              "placeholder": "Select asset type"
            },
            "account": {
              "label": "Account",
              "placeholder": "Select account",
              "required": "Account is required"
            },
            "date": {
              "label": "Date",
              "placeholder": "Select date",
              "required": "Date is required"
            },
            "note": {
              "label": "Note",
              "placeholder": "Optional note"
            },
            "convertedAmount": "Base amount",
            "rateLabel": "Rate",
            "rateTimestamp": "As of",
            "rateTooltip": "Rates refresh automatically as you edit the amount, currency, or account. Some providers may update less frequently.",
            "baseCurrencyLabel": "Base",
            "fxSourceLabel": "Source",
            "refreshing": "Refreshing quote...",
            "staleRate": "Stale rate"
          },
          "actions": {
            "save": "Save",
            "cancel": "Cancel"
          },
          "messages": {
            "success": "✅ Expense saved",
            "error": "❌ {{error}}",
            "unknownError": "Unexpected error"
          }
        },
        "income": {
          "fields": {
            "amount": {
              "label": "Amount",
              "placeholder": "Enter amount",
              "required": "Amount is required",
              "hint": "Decimals are supported. Use a dot, for example: 12.34"
            },
            "category": {
              "label": "Category",
              "placeholder": "Select income source"
              ,
              "required": "Category is required",
              "hint": "Choose the business meaning of this cash inflow. Transfers and reconciliations are handled elsewhere."
            },
            "currency": {
              "label": "Currency",
              "placeholder": "Select currency",
              "required": "Currency is required"
            },
            "currencyRequired": "Currency is required",
            "assetType": {
              "label": "Asset type",
              "placeholder": "Select asset type"
            },
            "account": {
              "label": "To Account",
              "placeholder": "Select target account",
              "required": "Account is required"
            },
            "date": {
              "label": "Date",
              "placeholder": "Select date",
              "required": "Date is required"
            },
            "note": {
              "label": "Note",
              "placeholder": "Optional note"
            },
            "convertedAmount": "Base amount",
            "rateLabel": "Rate",
            "rateTimestamp": "As of",
            "refreshing": "Refreshing quote...",
            "staleRate": "Stale rate"
          },
          "actions": {
            "save": "Save",
            "cancel": "Cancel"
          },
          "messages": {
            "success": "✅ Income saved",
            "error": "❌ {{error}}",
            "unknownError": "Unexpected error"
          }
        }
      }
    }
  },
  "uk": {
    "transactionsHub": {
      "pageTitle": "Хаб транзакцій",
      "pageDescription": "Швидко фіксуйте нові рухи та тримайте останню активність у читабельному вигляді.",
      "actionsTitle": "Швидкі дії",
      "actionsDescription": "Почніть додавання витрати або доходу, не виходячи з робочого простору транзакцій.",
      "addExpense": "Додати витрату",
      "addIncome": "Додати дохід",
      "addTransfer": "Переказ між рахунками",
      "recentTitle": "Нещодавні транзакції",
      "recentDescription": "Переглядайте останні рухи, виправлення та внески до цілей в одному місці.",
      "expenseModalTitle": "Додати нову витрату",
      "incomeModalTitle": "Додати новий дохід",
      "loadMore": "Завантажити ще",
      "loadingMore": "Завантажуємо ще транзакції...",
      "scrollForMore": "Прокрутіть нижче, щоб завантажити ще транзакції",
      "endOfList": "Це кінець списку нещодавніх транзакцій.",
      "expenseSuccess": "Витрату успішно збережено.",
      "incomeSuccess": "Дохід успішно збережено.",
      "empty": "Поки немає транзакцій. Додайте першу, щоб почати.",
      "loadError": "Не вдалося завантажити транзакції.",
      "unnamed": "Транзакція",
      "type": {
        "expense": "Витрата",
        "income": "Дохід",
        "transfer": "Переказ",
        "other": "Транзакція"
      },
      "categories": {
        "manualTransfer": "Ручний переказ",
        "reconcile": "Коригування звірки",
        "goal": "Внесок до цілі"
      },
      "accountBadge": {
        "cardLabel": "Картка",
        "transferLabel": "Маршрут переказу",
        "from": "Звідки",
        "to": "Куди",
        "unknown": "Невідомий рахунок"
      },
      "lockedBoundary": "Заблоковано · до {{date}}",
      "locked": "Заблоковано",
      "revaluePending": "Очікує переоцінки",
      "valuation": {
        "estimatedIn": "Оцінка в {{currency}}"
      },
      "undoGoalContribution": "Скасувати внесок",
      "undoTransfer": "Скасувати переказ",
      "undoConfirmTitle": "Скасувати внесок до цілі?",
      "undoConfirmDescription": "Це видалить переказ і оновить прогрес цілі.",
      "undoConfirmOk": "Скасувати внесок",
      "undoTransferConfirmTitle": "Скасувати переказ?",
      "undoTransferConfirmDescription": "Це видалить запис переказу.",
      "undoTransferConfirmOk": "Скасувати переказ",
      "undoConfirmCancel": "Залишити переказ",
      "undoExpiredHint": "Скасування доступне протягом 24 годин після переказу.",
      "undoSuccess": "Внесок скасовано.",
      "undoError": "Не вдалося скасувати внесок.",
      "reviewQueue": {
        "title": "Черга для уточнення",
        "alertTitle": "Кільком транзакціям ще потрібна категорія",
        "alertBody": "{{count}} транзакцій потребують фінальної категорії, щоб звіти показували їх правильно.",
        "operationalTitle": "Сама транзакція вже збережена правильно",
        "operationalBody": "Тут потрібно лише вибрати, як вона має виглядати у звітах. Сума, дата та рух грошей не зміняться.",
        "breakdownTitle": "Що ще треба уточнити",
        "loading": "Перевіряємо транзакції, які ще потребують вашого рішення...",
        "loadError": "Не вдалося завантажити чергу для уточнення.",
        "resolve": "Зберегти категорію",
        "markReviewed": "Залишити без категорії",
        "resolveSuccess": "Категорію збережено.",
        "bulkResolveSuccess": "Групу оновлено.",
        "resolveError": "Не вдалося оновити цей запис.",
        "currentMeaning": "Поточна категорія у звітах",
        "bulkCurrentMeaning": "Поточна категорія для цієї групи",
        "unassigned": "Не призначено",
        "businessPlaceholder": "Оберіть найкращу категорію",
        "reportingPlaceholder": "Оберіть категорію лише для звітів",
        "bulkTitle": "Виправити повтори разом",
        "bulkBody": "{{count}} груп повторюваних записів можна оновити за один раз.",
        "bulkResolve": "Зберегти для всієї групи",
        "bulkTag": "{{count}} транзакцій",
        "whatToDo": "Оберіть категорію, яка найкраще описує цю транзакцію у ваших звітах.",
        "bulkWhatToDo": "Оберіть одну категорію та застосуйте її до всієї цієї групи.",
        "sources": {
          "manualEntry": "Ручний запис",
          "recurringTask": "Повторюване завдання",
          "reconciliation": "Коригування балансу",
          "other": "Потрібна перевірка"
        },
        "reasons": {
          "reconciliationUnclassified": {
            "label": "Коригуванню балансу потрібна мітка для звітів",
            "body": "Цей запис виправляє баланс. Додайте категорію лише для звітів, якщо хочете бачити його в певній групі."
          },
          "recurringMissingMeaning": {
            "label": "Повторюваному запису потрібна категорія",
            "body": "Цій повторюваній транзакції ще потрібна категорія, яку мають використовувати звіти."
          },
          "missingUserCategory": {
            "label": "Категорію ще не вибрано",
            "body": "Оберіть категорію, яка найкраще описує цю транзакцію, щоб звіти показували її правильно."
          },
          "generic": {
            "label": "Потрібна перевірка",
            "body": "Цій транзакції ще потрібна фінальна категорія для звітів."
          }
        }
      },
      "transfer": {
        "title": "Переказ між рахунками",
        "defaultNote": "Переказ між рахунками",
        "unknownAccount": "Невідомий рахунок",
        "noticeTitle": "Перемістіть гроші між своїми рахунками",
        "noticeBody": "Переказ змінює баланси рахунків, але не рахується як дохід або витрата у звітах.",
        "notEnoughAccountsTitle": "Спочатку додайте ще один рахунок",
        "notEnoughAccountsBody": "Для переказу потрібно щонайменше два активні рахунки.",
        "fromEyebrow": "Гроші виходять",
        "toEyebrow": "Гроші приходять",
        "fields": {
          "from": {
            "label": "Звідки",
            "placeholder": "Оберіть рахунок списання",
            "required": "Оберіть, звідки йдуть гроші"
          },
          "to": {
            "label": "Куди",
            "placeholder": "Оберіть рахунок зарахування",
            "required": "Оберіть, куди йдуть гроші"
          },
          "amount": {
            "label": "Сума",
            "placeholder": "0.00",
            "required": "Введіть суму переказу",
            "invalid": "Введіть коректну суму більше нуля",
            "hint": "Підтримуються лише перекази між рахунками в одній валюті."
          },
          "date": {
            "label": "Дата",
            "required": "Оберіть дату переказу"
          },
          "note": {
            "label": "Нотатка",
            "placeholder": "Необовʼязкова нотатка, наприклад поповнення готівки"
          }
        },
        "balance": {
          "current": "Поточний баланс",
          "available": "Доступно",
          "currency": "Валюта"
        },
        "preview": {
          "fromAfter": "На рахунку списання після переказу",
          "toAfter": "На рахунку зарахування після переказу",
          "availableAfter": "Доступно після: {{amount}}",
          "notIncome": "Внутрішній рух, не дохід"
        },
        "actions": {
          "submit": "Створити переказ",
          "cancel": "Скасувати",
          "swap": "Поміняти рахунки"
        },
        "messages": {
          "success": "Переказ виконано.",
          "error": "Не вдалося створити переказ: {{error}}",
          "unknownError": "Невідома помилка",
          "accountsLoadError": "Не вдалося завантажити рахунки для переказу."
        }
      },
      "forms": {
        "expense": {
          "fields": {
            "amount": {
              "label": "Сума",
              "placeholder": "Введіть суму",
              "required": "Вкажіть суму",
              "hint": "Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34"
            },
            "category": {
              "label": "Категорія",
              "placeholder": "Оберіть категорію",
              "required": "Категорія обовʼязкова"
            },
            "currency": {
              "label": "Валюта",
              "placeholder": "Оберіть валюту",
              "required": "Валюта обовʼязкова"
            },
            "currencyRequired": "Валюта обовʼязкова",
            "assetType": {
              "label": "Тип активу",
              "placeholder": "Оберіть тип активу"
            },
            "account": {
              "label": "Рахунок",
              "placeholder": "Оберіть рахунок",
              "required": "Рахунок обовʼязковий"
            },
            "date": {
              "label": "Дата",
              "placeholder": "Оберіть дату",
              "required": "Дата обовʼязкова"
            },
            "note": {
              "label": "Нотатка",
              "placeholder": "Необовʼязкова нотатка"
            },
            "convertedAmount": "Базова сума",
            "rateLabel": "Курс",
            "rateTimestamp": "Станом на",
            "rateTooltip": "Котирування оновлюються автоматично під час зміни суми, валюти чи рахунку. Деякі джерела можуть оновлюватися рідше.",
            "baseCurrencyLabel": "База",
            "fxSourceLabel": "Джерело",
            "refreshing": "Оновлюємо котирування...",
            "staleRate": "Застарілий курс"
          },
          "actions": {
            "save": "Зберегти",
            "cancel": "Скасувати"
          },
          "messages": {
            "success": "✅ Витрату збережено",
            "error": "❌ {{error}}",
            "unknownError": "Невідома помилка"
          }
        },
        "income": {
          "fields": {
            "amount": {
              "label": "Сума",
              "placeholder": "Введіть суму",
              "required": "Вкажіть суму",
              "hint": "Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34"
            },
            "category": {
              "label": "Категорія",
              "placeholder": "Оберіть джерело доходу"
              ,
              "required": "Категорія обовʼязкова",
              "hint": "Оберіть business meaning цього надходження. Перекази та звірки оформлюються окремо."
            },
            "currency": {
              "label": "Валюта",
              "placeholder": "Оберіть валюту",
              "required": "Валюта обовʼязкова"
            },
            "currencyRequired": "Валюта обовʼязкова",
            "assetType": {
              "label": "Тип активу",
              "placeholder": "Оберіть тип активу"
            },
            "account": {
              "label": "На рахунок",
              "placeholder": "Оберіть рахунок зарахування",
              "required": "Рахунок обовʼязковий"
            },
            "date": {
              "label": "Дата",
              "placeholder": "Оберіть дату",
              "required": "Дата обовʼязкова"
            },
            "note": {
              "label": "Нотатка",
              "placeholder": "Необовʼязкова нотатка"
            },
            "convertedAmount": "Базова сума",
            "rateLabel": "Курс",
            "rateTimestamp": "Станом на",
            "refreshing": "Оновлюємо котирування...",
            "staleRate": "Застарілий курс"
          },
          "actions": {
            "save": "Зберегти",
            "cancel": "Скасувати"
          },
          "messages": {
            "success": "✅ Дохід збережено",
            "error": "❌ {{error}}",
            "unknownError": "Невідома помилка"
          }
        }
      }
    }
  }
};

export default resources;
